import { api, getBackendUrl } from './api.js';
import { authState } from './auth.js';
import { createSSEConnection } from './sse.js';

/**
 * Browsers don't reliably sniff a MIME type for some formats — most notably
 * PSD, where Chrome/Safari often report `application/octet-stream` or "" —
 * which would be rejected by the server's allowed-MIME list. Map known
 * extensions to a canonical type so the upload flow uses a recognized
 * Content-Type end-to-end.
 */
const EXTENSION_MIME_FALLBACKS = {
    psd: 'image/vnd.adobe.photoshop',
};

function inferContentType(file) {
    if (file.type && file.type !== 'application/octet-stream') return file.type;
    const dot = file.name.lastIndexOf('.');
    if (dot > 0 && dot < file.name.length - 1) {
        const ext = file.name.slice(dot + 1).toLowerCase();
        if (EXTENSION_MIME_FALLBACKS[ext]) return EXTENSION_MIME_FALLBACKS[ext];
    }
    return file.type;
}

/**
 * Compute SHA-256 hash of a File using SubtleCrypto.
 * @param {File} file
 * @returns {Promise<string>} hex-encoded hash
 */
export async function computeFileHash(file) {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Upload a file using the presigned upload flow.
 * Returns the completed upload with file metadata.
 *
 * Flow:
 * 1. Compute SHA-256 hash of the file
 * 2. Call POST /api/uploads/initiate with hash
 * 3. If deduped (status=completed), return immediately
 * 4. PUT file to the upload URL (presigned S3 or local transfer)
 * 5. Poll GET /api/uploads/:id/status until completed
 *
 * @param {File} file
 * @param {object} [options]
 * @param {(progress: number) => void} [options.onProgress] - Called with 0-1 progress during upload
 * @param {(phase: string) => void} [options.onPhase] - Called when phase changes: 'hashing', 'uploading', 'processing'
 * @param {{post: Function, get: Function, baseUrl?: string} | null} [options.api] - Optional API
 *   client override. Defaults to the assets API client.
 * @returns {Promise<{upload_id: string, status: string, file?: {width: number, height: number, file_size: number, mime_type: string}}>}
 */
export async function uploadFile(file, options = {}) {
    const apiClient = options.api ?? api;
    const baseUrl = apiClient.baseUrl ?? getBackendUrl();

    // 1. Compute hash
    options.onPhase?.('hashing');
    const contentHash = await computeFileHash(file);
    const contentType = inferContentType(file);

    // 2. Initiate upload
    const initResult = await apiClient.post('/api/uploads/initiate', {
        original_name: file.name,
        content_type: contentType,
        content_hash: contentHash,
    });

    // 3. If already deduped, poll for file metadata and return
    if (initResult.status === 'completed') {
        const status = await apiClient.get(`/api/uploads/${initResult.upload_id}/status`);
        return status;
    }

    // 4. Upload file to the provided URL (uses XHR for progress tracking)
    options.onPhase?.('uploading');
    const rawUploadUrl = initResult.upload_url;
    const isLocal = rawUploadUrl.startsWith('/api/');
    const uploadUrl = isLocal ? `${baseUrl}${rawUploadUrl}` : rawUploadUrl;
    const xhrToken = isLocal ? await authState.getAccessToken() : null;
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', contentType);
        if (xhrToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${xhrToken}`);
        }
        if (options.onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    options.onProgress(e.loaded / e.total);
                }
            };
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload failed: ${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
    });

    // 5. Wait for processing via SSE
    options.onPhase?.('processing');
    const token = await authState.getAccessToken();
    return new Promise((resolve, reject) => {
        const sse = createSSEConnection(`/api/uploads/${initResult.upload_id}/status`, {
            baseUrl,
            onEvent: (data) => {
                if (data.status === 'completed') {
                    sse.disconnect();
                    resolve(data);
                } else if (data.status === 'failed') {
                    sse.disconnect();
                    reject(new Error('Upload processing failed'));
                }
            },
            onError: () => {
                sse.disconnect();
                reject(new Error('Upload status connection failed'));
            },
        });
        sse.connect(token);
    });
}
